import { ref, computed, type Ref, type ComputedRef } from 'vue'
import * as Sentry from '@sentry/nuxt'
import type {
  User,
  UserIdentity,
  Session,
  SupabaseClient,
} from '@supabase/supabase-js'
import { ensureSolanaWalletInitialized } from '~/utils/solanaWalletInit'

// `solana-wallets-vue` is imported lazily at the callsite inside the two
// SIWS methods below. A module-top import drags the whole Solana stack
// (@solana/web3.js → bs58 → safe-buffer) into the SSR bundle, where Vite's
// Rollup CJS-shim for safe-buffer fails to provide a working Buffer.from
// reference at function cold-start (`TypeError: Cannot read properties
// of undefined (reading 'from')`). The Solana flow is user-driven and
// client-only; dynamic import keeps it out of the server bundle entirely.

/**
 * Auth method tracked locally so the backend free-bet/anti-abuse logic
 * (auth-login) can attribute new accounts to a signup source. Mirrors the
 * shape `usePrivy.ts` exposed so `useAuth.ts` (T31) can swap imports
 * without changing call sites.
 */
export type AuthMethod = 'email' | 'google' | 'wallet_evm' | 'wallet_solana'
export type AuthChainType = 'ethereum' | 'solana' | null
export type OAuthProvider = 'google'

// ─── Module-level reactive state (singleton) ────────────────────────
//
// Same singleton pattern as `usePrivy.ts` — a single shared auth state
// across all component instances. The Nuxt plugin `$supabase` lives on
// nuxtApp, so module-level refs here are safe in the SPA context. SSR
// safety is provided by importing this composable only in client-side
// code paths (the consuming pages/composables all run client-side).

const user = ref<User | null>(null)
const initialized = ref(false)
let subscribed = false

/**
 * Provider strings that Supabase Auth's `signInWithWeb3` writes into
 * `auth.identities.provider`. **§13.1 BLOCKER** (open item in the spec):
 * the exact strings are TBD until a real MetaMask + Phantom sign-in is
 * captured on staging. Until then we maintain a permissive list — both
 * lowercase chain names and the Supabase EIP-/SIWS-style names. T31's
 * staging probe will tighten this list. DO NOT remove the TODO until
 * §13.1 is closed in the spec.
 *
 * TODO(§13.1): replace with the canonical strings observed on staging.
 */
// ─── Web3 identity helpers (EXPORTED — shared with profile.vue etc) ─
//
// GoTrue writes BOTH Ethereum and Solana identities with `provider='web3'`;
// the chain itself lives in `identity_data.custom_claims.chain` (confirmed
// against a live row 2026-05-17 — see migration 130006). We also tolerate
// the older provider literals `'ethereum'` / `'eip155'` / `'solana'` in case
// any data source still emits them, and a `sub`-based fallback for any
// row that doesn't expose `custom_claims`.

export type Web3Chain = 'evm' | 'solana'

export function extractWeb3Chain(identity: UserIdentity): Web3Chain | null {
  if (identity.provider === 'ethereum' || identity.provider === 'eip155') return 'evm'
  if (identity.provider === 'solana') return 'solana'
  if (identity.provider !== 'web3') return null
  const data = identity.identity_data ?? {}
  const customClaims = data.custom_claims as Record<string, unknown> | undefined
  const ccChain = (customClaims && typeof customClaims === 'object') ? customClaims.chain : undefined
  const raw = typeof ccChain === 'string'
    ? ccChain
    : (typeof data.sub === 'string' ? (data.sub.match(/^web3:([^:]+):/i)?.[1] ?? null) : null)
  if (!raw) return null
  const lower = raw.toLowerCase()
  if (lower === 'ethereum' || lower === 'evm') return 'evm'
  if (lower === 'solana') return 'solana'
  return null
}

/**
 * Extract the wallet address and chain from an identity row. Address is
 * pre-normalized per chain: EVM lowercased (per `feedback_evm_address_casing`),
 * Solana left as-is (base58 is case-sensitive).
 */
export function extractWeb3Wallet(identity: UserIdentity): { chain: Web3Chain; address: string } | null {
  const chain = extractWeb3Chain(identity)
  if (!chain) return null
  const data = identity.identity_data ?? {}
  const customClaims = data.custom_claims as Record<string, unknown> | undefined
  let raw: string | undefined
  if (customClaims && typeof customClaims === 'object' && typeof customClaims.address === 'string') {
    raw = customClaims.address
  } else if (typeof data.address === 'string') {
    raw = data.address
  } else if (typeof data.wallet_address === 'string') {
    raw = data.wallet_address
  } else if (typeof data.sub === 'string') {
    raw = data.sub.match(/^web3:[^:]+:(.+)$/i)?.[1]
  }
  if (!raw) return null
  return { chain, address: chain === 'evm' ? raw.toLowerCase() : raw }
}

function isEthereumIdentity(identity: UserIdentity): boolean {
  return extractWeb3Chain(identity) === 'evm'
}
function isSolanaIdentity(identity: UserIdentity): boolean {
  return extractWeb3Chain(identity) === 'solana'
}

// ─── SIWE message builder (EIP-4361) ────────────────────────────────
//
// Inlined because no existing helper covers the client side — the only
// other parser lives in the `user-link-wallet` edge function (and only
// PARSES, doesn't BUILD). The `Nonce:` line label MUST stay exact: that
// edge function (apps/supabase/supabase/functions/user-link-wallet/index.ts:137)
// matches the literal "Nonce" label when consuming the single-use nonce.

interface SiweMessageArgs {
  domain: string
  address: string
  statement: string
  uri: string
  version: string
  chainId: number
  nonce: string
  issuedAt: string
}

function buildSiweMessage(args: SiweMessageArgs): string {
  return (
    `${args.domain} wants you to sign in with your Ethereum account:\n` +
    `${args.address}\n` +
    `\n` +
    `${args.statement}\n` +
    `\n` +
    `URI: ${args.uri}\n` +
    `Version: ${args.version}\n` +
    `Chain ID: ${args.chainId}\n` +
    `Nonce: ${args.nonce}\n` +
    `Issued At: ${args.issuedAt}`
  )
}

/** Hex nonce, 16 chars, used for initial SIWE login (not the link-wallet flow). */
function randomNonceHex(): string {
  const bytes = new Uint8Array(8)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Composable for Supabase Auth — replaces `usePrivy.ts`.
 *
 * Public surface mirrors `usePrivy.ts` so `useAuth.ts` (T31) only needs
 * an import swap. See spec §6.3 + §5.13.4.
 */
export function useSupabaseAuth() {
  // The `$supabase` plugin is `.client.ts` — undefined during SSR / pre-
  // hydration. Composables that read it (useGame, etc.) are called at
  // layout setup time, including server-side, so guard rather than crash.
  // Methods on the returned object (loginWithEmailCode, logout, …) all
  // touch `$supabase` directly — they would crash if invoked from SSR
  // code paths, but they're only called from user event handlers which
  // never run during render.
  const { $supabase } = useNuxtApp() as { $supabase?: SupabaseClient }

  // First call hydrates from current session and subscribes to changes.
  // Skipped on SSR (no plugin instance to subscribe to) — the module-level
  // `user`/`initialized` refs stay at their safe defaults until the first
  // client-side call (post-hydration) takes this branch.
  if ($supabase && !subscribed) {
    subscribed = true
    void $supabase.auth.getSession().then(({ data }: { data: { session: Session | null } }) => {
      user.value = data.session?.user ?? null
      initialized.value = true
    })
    $supabase.auth.onAuthStateChange((_event, session: Session | null) => {
      user.value = session?.user ?? null
    })
  }

  const isAuthenticated: ComputedRef<boolean> = computed(() => user.value !== null)

  // ─── Identity-derived computeds (§13.1 — STUBBED) ────────────────
  //
  // These mirror what `usePrivy.ts` exposed off `linked_accounts`. With
  // Supabase Auth they read from `user.identities[]`. The provider strings
  // and identity_data keys are STILL TBD per §13.1 — the helpers above
  // are permissive stubs. Behavior is conservative: returns null when
  // nothing matches, never throws.

  const walletAddress: ComputedRef<string | null> = computed(() => {
    if (!user.value?.identities) return null
    const eth = user.value.identities.find(isEthereumIdentity)
    return eth ? (extractWeb3Wallet(eth)?.address ?? null) : null
  })

  const solanaAddress: ComputedRef<string | null> = computed(() => {
    if (!user.value?.identities) return null
    const sol = user.value.identities.find(isSolanaIdentity)
    return sol ? (extractWeb3Wallet(sol)?.address ?? null) : null
  })

  const authChainType: ComputedRef<AuthChainType> = computed(() => {
    if (!user.value?.identities) return null
    if (user.value.identities.some(isSolanaIdentity)) return 'solana'
    if (user.value.identities.some(isEthereumIdentity)) return 'ethereum'
    return null
  })

  const authMethod: ComputedRef<AuthMethod | null> = computed(() => {
    const u = user.value
    if (!u?.identities) return null
    // Match `detectAuthMethod` priority from usePrivy.ts: email → google → wallet.
    if (u.identities.some((i) => i.provider === 'email')) return 'email'
    if (u.identities.some((i) => i.provider === 'google')) return 'google'
    if (u.identities.some(isSolanaIdentity)) return 'wallet_solana'
    if (u.identities.some(isEthereumIdentity)) return 'wallet_evm'
    return null
  })

  // ─── Email OTP ───────────────────────────────────────────────────

  async function sendEmailCode(email: string): Promise<boolean> {
    const { error } = await $supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(),
      options: { shouldCreateUser: true },
    })
    if (error) throw error
    return true
  }

  async function loginWithEmailCode(email: string, code: string): Promise<void> {
    const { error } = await $supabase.auth.verifyOtp({
      email: email.trim().toLowerCase(),
      token: code.trim(),
      type: 'email',
    })
    if (error) throw error
  }

  // ─── Google OAuth (PKCE) ─────────────────────────────────────────

  async function loginWithOAuth(provider: OAuthProvider): Promise<void> {
    const { error } = await $supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    })
    if (error) throw error
    // Browser redirects to provider; callback page handles the rest.
  }

  /**
   * Shim for caller compatibility. PKCE auto-exchange happens inside
   * the unified plugin via `detectSessionInUrl: true`, so this just
   * waits for the session to materialize. Resolves with no value.
   *
   * Old usePrivy signature was `(code, state, provider)`; all three
   * are ignored here. T31's `useAuth.completeOAuthLogin()` will
   * trigger the backend handshake separately after this resolves.
   */
  async function completeOAuthLogin(
    _code?: string,
    _state?: string,
    _urlProvider?: string,
  ): Promise<void> {
    const { data } = await $supabase.auth.getSession()
    if (data.session) return
    await new Promise<void>((resolve) => {
      const { data: sub } = $supabase.auth.onAuthStateChange((event, session) => {
        if (event === 'SIGNED_IN' && session) {
          sub.subscription.unsubscribe()
          resolve()
        }
      })
    })
  }

  // ─── EVM SIWE (manual) ───────────────────────────────────────────

  async function loginWithSiwe(
    address: string,
    chainId: string,
    signMessage: (message: string) => Promise<string>,
    _walletClientType?: string,
  ): Promise<void> {
    const numericChainId = parseInt(chainId.replace('eip155:', ''), 10)
    const message = buildSiweMessage({
      domain: window.location.host,
      address,
      statement: 'Sign in to Aurum Run',
      uri: window.location.origin,
      version: '1',
      chainId: numericChainId,
      nonce: randomNonceHex(),
      issuedAt: new Date().toISOString(),
    })
    const signature = await signMessage(message)
    const { error } = await $supabase.auth.signInWithWeb3({
      chain: 'ethereum',
      message,
      // Supabase types `signature` as `Hex` (`0x${string}`); the runtime check
      // is just "must be a 0x-prefixed hex string", which our wallet-side
      // `signMessage` callbacks already produce.
      signature: signature as `0x${string}`,
    })
    if (error) throw error
  }

  // ─── Solana SIWS ─────────────────────────────────────────────────

  /**
   * Solana SIWS — passes the wallet object from `solana-wallets-vue`
   * straight to `signInWithWeb3`.
   *
   * ⚠️ §13.2 BLOCKER: this assumes Supabase's SDK accepts the
   * solana-wallets-vue wallet object as a `WalletStandard`-compatible
   * input. Staging verification (T53) is required. If it fails, swap
   * this body for the manual fallback below — extract the wallet's
   * `signMessage`, build a SIWS message ourselves (same `buildSiweMessage`
   * shape but with the SIWS header / Solana address), and pass
   * `{ chain: 'solana', message, signature }`.
   *
   * TODO(§13.2): confirm `wallet:` form on staging; otherwise switch
   * to the `{ message, signature }` path described above.
   *
   * The original `usePrivy.ts` signature took `(publicKey, signMessage)`;
   * both are preserved as ignored params so the callsite in `LoginModal.vue`
   * doesn't need to change in T31.
   */
  async function loginWithSiws(
    _publicKeyBase58: string,
    _signMessage: unknown,
  ): Promise<void> {
    await ensureSolanaWalletInitialized()
    const { useWallet } = await import('solana-wallets-vue')
    const { wallet } = useWallet()
    // solana-wallets-vue's `wallet.value` is the Wallet wrapper (just
    // `{ adapter, readyState }`). supabase-js's signInWithWeb3 for Solana
    // requires the wallet argument to expose `signIn` (Wallet Standard) or
    // `signMessage` + `publicKey.toBase58()` at the TOP LEVEL — those live
    // on `.adapter`, not on the wrapper. Hand over the adapter directly.
    const adapter = wallet.value?.adapter
    if (!adapter) {
      throw new Error('Solana wallet not connected')
    }
    const { error } = await $supabase.auth.signInWithWeb3({
      chain: 'solana',
      statement: 'Sign in to Aurum Run',
      wallet: adapter as never,
    })
    if (error) throw error
  }

  // ─── Email + Link methods (§5.13.4) ──────────────────────────────

  async function sendLinkEmailCode(email: string): Promise<boolean> {
    const { error } = await $supabase.auth.updateUser({
      email: email.trim().toLowerCase(),
    })
    if (error) throw error
    return true
  }

  async function linkEmailWithCode(email: string, code: string): Promise<void> {
    const normalizedEmail = email.trim().toLowerCase()
    const { error } = await $supabase.auth.verifyOtp({
      email: normalizedEmail,
      token: code.trim(),
      type: 'email_change',
    })
    if (error) throw error

    // Backend bookkeeping (free-bet + visitor anti-abuse). Non-fatal —
    // a failure here leaves the auth.users row with the new email anyway.
    await notifyEmailLink(normalizedEmail)
  }

  async function updateEmailWithCode(
    _oldAddress: string,
    email: string,
    code: string,
  ): Promise<void> {
    // Same flow as linkEmailWithCode: Supabase's email change is the
    // same verifyOtp({ type: 'email_change' }) regardless of whether
    // the user already had an email. `_oldAddress` is preserved in the
    // signature for caller compatibility (usePrivy needed it).
    await linkEmailWithCode(email, code)
  }

  async function notifyEmailLink(email: string): Promise<void> {
    try {
      const token = await getAccessToken()
      if (!token) return
      const config = useRuntimeConfig()
      // try/catch defends against the prod SSR runtime's truthy partial `window`, where reading localStorage throws; falls back to null as before.
      let sessionId: string | null = null
      try {
        sessionId = window.localStorage.getItem('aurumrun_session_id')
      } catch {
        sessionId = null
      }
      await $fetch(`${config.public.apiBase}/user-link-method`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
        },
        body: { method: 'email', email },
      })
    } catch (err) {
      // Non-fatal for the auth flow: auth.users already carries the new email,
      // and re-running the OTP dance to retry would be worse than proceeding.
      // But it is NOT harmless — this call is what mirrors the address onto
      // public.users, so a failure here leaves the user verified on Supabase
      // and email-less everywhere the product actually looks (withdrawal gate,
      // /auth-session, notifications) while the UI still reports success.
      // Report it so the silent half-linked state is visible instead of
      // sitting in one user's console.
      console.error('[useSupabaseAuth] user-link-method notify failed:', err)
      Sentry.captureException(err, {
        tags: { flow: 'auth', action: 'user-link-method' },
      })
    }
  }

  async function linkEvmWallet(
    address: string,
    chainId: string,
    signMessage: (message: string) => Promise<string>,
  ): Promise<void> {
    const lowerAddress = address.toLowerCase()
    const numericChainId = parseInt(chainId.replace('eip155:', ''), 10)
    const nonce = await fetchLinkWalletNonce()
    const message = buildSiweMessage({
      domain: window.location.host,
      address,
      statement: 'Link this wallet to your Aurum Run account',
      uri: window.location.origin,
      version: '1',
      chainId: numericChainId,
      nonce,
      issuedAt: new Date().toISOString(),
    })
    const signature = await signMessage(message)
    await postLinkWallet({
      chain: 'ethereum',
      walletAddress: lowerAddress,
      message,
      signature,
    })
  }

  /**
   * Solana wallet link. Stub for §13.2 — the wallet object form is the
   * happy path. If staging shows `signInWithWeb3` doesn't accept the
   * solana-wallets-vue wallet, this needs the same manual SIWS fallback
   * described on `loginWithSiws`. Different from `loginWithSiws` though:
   * this flow always uses a server-issued nonce so the edge function
   * can verify replay-safety, so the manual message-building path is
   * the same regardless.
   *
   * TODO(§13.2 + §13.1): finalize SIWS message header + signature
   * encoding once Web3 is enabled on staging.
   */
  async function linkSolanaWallet(): Promise<void> {
    await ensureSolanaWalletInitialized()
    const { useWallet } = await import('solana-wallets-vue')
    const { wallet, publicKey, signMessage } = useWallet() as unknown as {
      wallet: Ref<{ name: string } | null>
      publicKey: Ref<{ toBase58: () => string } | null>
      signMessage: Ref<((msg: Uint8Array) => Promise<Uint8Array>) | null>
    }
    if (!wallet.value || !publicKey.value || !signMessage.value) {
      throw new Error('Solana wallet not connected')
    }
    const address = publicKey.value.toBase58()
    const nonce = await fetchLinkWalletNonce()
    // SIWS uses the same EIP-4361 grammar as SIWE per the edge function's
    // parser (apps/supabase/supabase/functions/user-link-wallet/index.ts) —
    // header just changes "Ethereum" to "Solana".
    const message =
      `${window.location.host} wants you to sign in with your Solana account:\n` +
      `${address}\n` +
      `\n` +
      `Link this wallet to your Aurum Run account\n` +
      `\n` +
      `URI: ${window.location.origin}\n` +
      `Version: 1\n` +
      `Nonce: ${nonce}\n` +
      `Issued At: ${new Date().toISOString()}`
    const sigBytes = await signMessage.value(new TextEncoder().encode(message))
    const signature = bytesToBase58(sigBytes)
    await postLinkWallet({
      chain: 'solana',
      walletAddress: address,
      message,
      signature,
    })
  }

  async function linkOAuth(provider: OAuthProvider): Promise<void> {
    const { error } = await $supabase.auth.linkIdentity({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    })
    if (error) throw error
  }

  async function fetchLinkWalletNonce(): Promise<string> {
    const token = await getAccessToken()
    if (!token) throw new Error('Not authenticated')
    const config = useRuntimeConfig()
    // try/catch defends against the prod SSR runtime's truthy partial `window`, where reading localStorage throws; falls back to null as before.
    let sessionId: string | null = null
    try {
      sessionId = window.localStorage.getItem('aurumrun_session_id')
    } catch {
      sessionId = null
    }
    const response = await $fetch<{ nonce: string }>(
      `${config.public.apiBase}/user-link-wallet-nonce`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
        },
      },
    )
    return response.nonce
  }

  async function postLinkWallet(body: {
    chain: 'ethereum' | 'solana'
    walletAddress: string
    message: string
    signature: string
  }): Promise<void> {
    const token = await getAccessToken()
    if (!token) throw new Error('Not authenticated')
    const config = useRuntimeConfig()
    // try/catch defends against the prod SSR runtime's truthy partial `window`, where reading localStorage throws; falls back to null as before.
    let sessionId: string | null = null
    try {
      sessionId = window.localStorage.getItem('aurumrun_session_id')
    } catch {
      sessionId = null
    }
    try {
      await $fetch(`${config.public.apiBase}/user-link-wallet`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          ...(sessionId ? { 'X-Session-Id': sessionId } : {}),
        },
        body,
      })
    } catch (err) {
      // $fetch / ofetch throws a FetchError on non-2xx whose `.message` is
      // a generic `[POST] "url": 409 Conflict`. The real typed error body
      // (`{error, message, statusCode, code}`) lives on `.data`. Re-throw
      // with the typed `code` baked into the message so callers (e.g.
      // profile.vue's mapLinkError) can pattern-match `WALLET_ALREADY_LINKED`
      // / `INVALID_SIGNATURE` / etc and render a friendly string.
      const body = (err as { data?: { code?: string; message?: string } }).data
      if (body?.code) {
        const friendly = body.message ?? 'Request failed'
        throw new Error(`${body.code}: ${friendly}`)
      }
      throw err
    }
  }

  // ─── Misc ────────────────────────────────────────────────────────

  async function getAccessToken(): Promise<string | null> {
    const { data } = await $supabase.auth.getSession()
    return data.session?.access_token ?? null
  }

  async function logout(): Promise<void> {
    await $supabase.auth.signOut({ scope: 'local' })
    user.value = null
  }

  return {
    // State
    user: user as Ref<User | null>,
    initialized,
    isAuthenticated,
    walletAddress,
    solanaAddress,
    authChainType,
    authMethod,
    // Email OTP
    sendEmailCode,
    loginWithEmailCode,
    // OAuth
    loginWithOAuth,
    completeOAuthLogin,
    // SIWE / SIWS
    loginWithSiwe,
    loginWithSiws,
    // Link methods
    sendLinkEmailCode,
    linkEmailWithCode,
    updateEmailWithCode,
    linkEvmWallet,
    linkSolanaWallet,
    linkOAuth,
    // Misc
    getAccessToken,
    logout,
  }
}

// ─── base58 encoder (Solana signature) ──────────────────────────────
//
// Inlined: only one caller (`linkSolanaWallet`), and pulling in `bs58` /
// `@solana/web3.js` just for this would be wasteful. ~25 LoC vs ~50 KB.
// Reference impl per Satoshi alphabet.
const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz'

function bytesToBase58(bytes: Uint8Array): string {
  if (bytes.length === 0) return ''
  // Count leading zeros — preserved as leading '1' chars in base58.
  let zeros = 0
  while (zeros < bytes.length && bytes[zeros] === 0) zeros++

  // Convert big-endian byte array to a digit-array (base 58) via long division.
  const digits: number[] = []
  for (const byte of bytes) {
    let carry = byte
    for (let j = 0; j < digits.length; j++) {
      carry += (digits[j] ?? 0) * 256
      digits[j] = carry % 58
      carry = Math.floor(carry / 58)
    }
    while (carry > 0) {
      digits.push(carry % 58)
      carry = Math.floor(carry / 58)
    }
  }

  let result = ''
  for (let i = 0; i < zeros; i++) result += '1'
  for (let i = digits.length - 1; i >= 0; i--) {
    result += BASE58_ALPHABET[digits[i] ?? 0]
  }
  return result
}
